import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Crown, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setL] = useState(null);

  useEffect(() => {
    api.get("/subscriptions/plans").then(r => setPlans(r.data));
  }, []);

  const subscribe = async (planId) => {
    if (!user) { nav("/signup"); return; }
    setL(planId);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe-hosted Checkout
      window.location.href = data.url;
    } catch (e) {
      setL(null);
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative">
      <img src="https://images.pexels.com/photos/7505924/pexels-photo-7505924.jpeg?auto=compress&cs=tinysrgb&w=2400" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/90 to-[#0A0A0A]" />
      <header className="relative z-10 px-6 lg:px-12 py-6">
        <Link to="/" className="text-[#E50914] font-black text-2xl">CINECINEMA</Link>
      </header>
      <main className="relative z-10 max-w-6xl mx-auto px-6 py-12 pb-24">
        <div className="text-center mb-14">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tighter">Choose your plan</h1>
          <p className="text-lg text-gray-300 mt-4">Cancel or change anytime. No hidden fees.</p>
          <p className="text-xs uppercase tracking-widest text-gray-500 mt-3">Secure checkout via Stripe</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`relative rounded-xl p-8 border transition-transform hover:scale-[1.02] ${
                p.popular ? "border-[#E50914] bg-gradient-to-b from-[#1a0808] to-[#0f0404]" : "border-white/10 bg-[#0f0f0f]"
              }`}
            >
              {p.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E50914] text-white text-xs uppercase tracking-widest font-bold px-3 py-1 rounded">Most Popular</div>
              )}
              {p.id === "premium" && <Crown className="w-8 h-8 text-yellow-500 mb-3" />}
              {p.id === "standard" && <Zap className="w-8 h-8 text-[#E50914] mb-3" />}
              <h3 className="text-2xl font-bold mb-2">{p.name}</h3>
              <div className="mb-6">
                <span className="text-5xl font-black">${p.price}</span>
                <span className="text-gray-400">/{p.interval}</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /> <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`subscribe-${p.id}`}
                onClick={() => subscribe(p.id)}
                disabled={loading === p.id || user?.subscription_tier === p.id}
                className={`w-full py-3 rounded font-semibold transition-colors ${
                  p.popular ? "btn-red" : "btn-glass"
                } ${user?.subscription_tier === p.id ? "opacity-60" : ""}`}
              >
                {user?.subscription_tier === p.id ? "Current Plan" : loading === p.id ? "Redirecting…" : "Subscribe"}
              </button>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
